import { useCallback } from 'react';
import axios from 'axios';

import useWaApi from './axios/useWaApi';
import { ReservationResponse, Workplace } from '../store/models/workplace';
import { WaWorkplaceRequestParams } from '../store/models/workplace/WaWorkplaceFilterParameter';
import {
  WaBranchSummaryResponse,
  WaSalesOfficeSummaryResponse,
  WaSummaryModel,
} from '../store/models/workplace/WaSummary';
import { WaWorkplaceStatus } from '../store/models/workplace/WaWorkplaceSearchOptions';
import {
  WaDisasterAreaBranchResponse,
  WaDisasterAreaSalesOfficeResponse,
} from '../models/WaSummary';
import { WaProduct } from '../store/models/product/WaProduct';

export interface ReservationParameters {
  engineerUserId: string;
  reserverUserId: string;
  disasterId: string;
  branchCode: string;
  customerId: string;
}

export interface SummaryParameters {
  branchCode: string;
  salesOfficeCode: string;
  sectionCode?: string;
}

export interface ProductStatusUpdateParameters {
  disasterId: string;
  customerId: string;
  branchCode: string;
  productNumbers: string[];
  newStatusId: string;
  newComment?: string;
}

export interface SummaryByBranchParameters {
  disasterId: string;
  branchCode?: string;
}

export interface SummaryBySalesOfficeParameters {
  disasterId: string;
  branchCode: string;
  salesOffice: string;
}

function cleanParams<T extends object>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params as Record<string, unknown>).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value === '  ') return true; // Allow Okinichi
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  ) as Partial<T>;
}

function useWaWorkplaceRepo() {
  const { post, get, patch } = useWaApi();

  const getWorkplacesRequest = useCallback(
    async (_parameters: WaWorkplaceRequestParams) => {
      try {
        return (await post<Workplace[]>('/api/wa/workplace', cleanParams(_parameters))) ?? [];
      } catch (error) {
        if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
          throw error;
        }
        console.log(error);
        return [];
      }
    },
    [post]
  );

  const getSearchOptionsProductStatusRequest = useCallback(async () => {
    try {
      return (await get<WaWorkplaceStatus[]>('/api/wa/options/workplace-product-statuses')) ?? [];
    } catch (error) {
      console.log(error);
      return [];
    }
  }, [get]);

  const postReserveWorkplacesRequest = useCallback(
    async (reservationParameters: ReservationParameters[]) => {
      try {
        return await post<ReservationResponse[]>(
          '/api/wa/workplaces/reservations/create',
          reservationParameters
        );
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    [post]
  );

  const postCancelReservationForWorkplaceRequest = useCallback(
    async (reservationParameters: ReservationParameters[]) => {
      try {
        return await post<ReservationResponse[]>(
          '/api/wa/workplaces/reservations/cancel',
          reservationParameters
        );
      } catch (error) {
        console.log(error);
        return;
      }
    },
    [post]
  );

  const getWorkplaceMobileSummaryRequest = useCallback(
    async (parameters: SummaryParameters) => {
      try {
        return (
          (await get<WaSummaryModel>('/api/wa/workplaces/summary/mobile', {
            params: cleanParams(parameters),
          })) ?? {
            numOfProducts: [],
            numOfSuspendedProducts: [],
            numOfStatusUnknownProducts: [],
            numOfHandledProducts: [],
            numOfProductsWithEntrapmentIssues: [],
          }
        );
      } catch (error) {
        console.log(error);
        return {
          numOfProducts: [],
          numOfSuspendedProducts: [],
          numOfStatusUnknownProducts: [],
          numOfHandledProducts: [],
          numOfProductsWithEntrapmentIssues: [],
        };
      }
    },
    [get]
  );

  const patchProductStatusRequest = useCallback(
    async (parameters: ProductStatusUpdateParameters) => {
      try {
        return (
          (await patch<{ workplace: Workplace; products: WaProduct[] }>(
            '/api/wa/status/update',
            parameters
          )) ?? { workplace: null, products: [] }
        );
      } catch (error) {
        console.log(error);
        return { workplace: null, products: [] };
      }
    },
    [patch]
  );

  const getSummaryByBranch = useCallback(
    async (parameters: SummaryByBranchParameters): Promise<WaBranchSummaryResponse[] | null> => {
      try {
        const response = await get<WaBranchSummaryResponse[]>(
          '/api/wa/workplaces/summary/pc/branch',
          {
            params: cleanParams(parameters),
          }
        );

        if (Array.isArray(response)) {
          return response;
        }

        return null;
      } catch (error) {
        console.error('REPO ERROR:', error);
        return null;
      }
    },
    [get]
  );

  const getSummaryBySalesOffice = useCallback(
    async (
      parameters: SummaryBySalesOfficeParameters
    ): Promise<WaSalesOfficeSummaryResponse[] | null> => {
      try {
        const response = await get<WaSalesOfficeSummaryResponse[]>(
          '/api/wa/workplaces/summary/pc/sales-office',
          {
            params: cleanParams(parameters),
          }
        );

        if (response && Array.isArray(response)) {
          return response;
        }

        return null;
      } catch (error) {
        console.error('Error fetching sales office summary:', error);
        return null;
      }
    },
    [get]
  );

  const getDisasterAreaSummaryByBranch = useCallback(
    async (disasterId: string): Promise<WaDisasterAreaBranchResponse[] | null> => {
      try {
        const response = await get<WaDisasterAreaBranchResponse[]>(
          '/api/wa/summary/disaster-area/branch',
          {
            params: { disasterId },
          }
        );

        if (Array.isArray(response)) {
          return response;
        }
        return null;
      } catch (error) {
        console.error('Error fetching disaster area summary by branch:', error);
        return null;
      }
    },
    [get]
  );

  const getDisasterAreaSummaryBySalesOffice = useCallback(
    async (
      disasterId: string,
      branchCode: string
    ): Promise<WaDisasterAreaSalesOfficeResponse | null> => {
      try {
        const response = await get<string[]>('/api/wa/summary/disaster-area/sales-office', {
          params: { disasterId, branchCode },
        });

        if (Array.isArray(response)) {
          return response;
        }

        return null;
      } catch (error) {
        console.error('Error fetching disaster area sales offices:', error);
        return null;
      }
    },
    [get]
  );

  return {
    getWorkplacesRequest,
    getSearchOptionsProductStatusRequest,
    postReserveWorkplacesRequest,
    postCancelReservationForWorkplaceRequest,
    getWorkplaceMobileSummaryRequest,
    patchProductStatusRequest,
    getSummaryByBranch,
    getSummaryBySalesOffice,
    getDisasterAreaSummaryByBranch,
    getDisasterAreaSummaryBySalesOffice,
  };
}

export default useWaWorkplaceRepo;
